import {
  AmbientLight,
  AxesHelper,
  Box3,
  Color,
  DirectionalLight,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  type Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  type Plane,
  Scene,
  Sphere,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { VIEWER_CONFIG } from '../config';
import { Measurements } from './Measurements';
import { collectMeshes, disposeModelTree, meshStats, showAllMeshes } from './modelOps';
import { Selection } from './Selection';
import { directionForView } from './standardViews';
import { pivotAtDepth, recenterOn } from './pivot';
import {
  distanceForVisibleHeight,
  orthoFrustum,
  visibleHeightAtDistance,
  type Projection,
} from './projection';

export interface ViewerStats {
  fps: number;
  /** True when nothing rendered in the last stats window (on-demand rendering). */
  idle: boolean;
  triangles: number;
  drawCalls: number;
  totalMeshes: number;
  visibleMeshes: number;
}

export type StatsListener = (stats: ViewerStats) => void;

export class Viewer {
  readonly scene: Scene;
  readonly perspectiveCamera: PerspectiveCamera;
  readonly orthographicCamera: OrthographicCamera;
  readonly renderer: WebGLRenderer;
  readonly controls: OrbitControls;
  readonly modelRoot: Group;
  readonly selection: Selection;
  readonly measurements: Measurements;

  private readonly canvas: HTMLCanvasElement;
  private readonly edgeMaterial = new LineBasicMaterial({
    color: VIEWER_CONFIG.edgeColor,
    transparent: true,
    opacity: 0.55,
  });
  private edgesEnabled = false;
  private resizeObserver: ResizeObserver | null = null;
  private rafId: number | null = null;
  private disposed = false;
  private statsListener: StatsListener | null = null;
  private cameraListener: (() => void) | null = null;
  private activeProjection: Projection = 'perspective';
  /** World height the orthographic frustum spans before its own zoom. */
  private orthoHeight = 10;
  private needsRender = true;
  private renderedFrames = 0;
  private lastStatsEmitTs = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.scene = new Scene();
    this.scene.background = new Color(VIEWER_CONFIG.background);

    const [cx, cy, cz] = VIEWER_CONFIG.initialCameraPosition;
    this.perspectiveCamera = new PerspectiveCamera(
      VIEWER_CONFIG.fov,
      1,
      VIEWER_CONFIG.near,
      VIEWER_CONFIG.far,
    );
    this.perspectiveCamera.position.set(cx, cy, cz);
    this.perspectiveCamera.lookAt(0, 0, 0);

    // the inactive camera is kept alive and re-synced on every switch
    // rather than rebuilt, so nothing holding a reference goes stale
    this.orthographicCamera = new OrthographicCamera(
      -1,
      1,
      1,
      -1,
      VIEWER_CONFIG.near,
      VIEWER_CONFIG.far,
    );
    this.orthographicCamera.position.copy(this.perspectiveCamera.position);
    this.orthographicCamera.quaternion.copy(this.perspectiveCamera.quaternion);

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, VIEWER_CONFIG.pixelRatioCap));

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = VIEWER_CONFIG.cameraDamping;
    this.controls.zoomToCursor = VIEWER_CONFIG.zoomToCursor;
    this.controls.addEventListener('change', () => {
      this.invalidate();
      this.cameraListener?.();
    });

    this.modelRoot = new Group();
    this.modelRoot.name = 'model-root';
    this.scene.add(this.modelRoot);

    this.selection = new Selection(
      canvas,
      () => this.camera,
      this.modelRoot,
      () => this.invalidate(),
    );
    this.measurements = new Measurements(
      canvas,
      () => this.camera,
      this.modelRoot,
      () => this.invalidate(),
    );
    this.scene.add(this.measurements.group);

    canvas.addEventListener('dblclick', this.onDoubleClick);

    this.addLighting();
    this.addHelpers();
    this.handleResize();
    this.observeResize();
    this.startLoop();
  }

  private addLighting(): void {
    const ambient = new AmbientLight(0xffffff, 0.6);
    this.scene.add(ambient);

    const dir = new DirectionalLight(0xffffff, 1.2);
    dir.position.set(50, 80, 30);
    this.scene.add(dir);

    const dir2 = new DirectionalLight(0xffffff, 0.4);
    dir2.position.set(-40, -20, -50);
    this.scene.add(dir2);
  }

  private addHelpers(): void {
    const axes = new AxesHelper(2);
    axes.name = 'axes';
    this.scene.add(axes);
  }

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.canvas);
  }

  /** Re-derives both cameras' frustums from the canvas size. */
  private applyCameraFrustum(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    const aspect = width / height;

    this.perspectiveCamera.aspect = aspect;
    this.perspectiveCamera.updateProjectionMatrix();

    const { left, right, top, bottom } = orthoFrustum(this.orthoHeight, aspect);
    this.orthographicCamera.left = left;
    this.orthographicCamera.right = right;
    this.orthographicCamera.top = top;
    this.orthographicCamera.bottom = bottom;
    // an orthographic frustum may extend behind the camera, which keeps
    // geometry from vanishing when the view is pushed inside the model
    this.orthographicCamera.near = -this.perspectiveCamera.far;
    this.orthographicCamera.far = this.perspectiveCamera.far;
    this.orthographicCamera.updateProjectionMatrix();
  }

  private handleResize(): void {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(width, height, false);
    this.applyCameraFrustum();
    this.invalidate();
  }

  /** The camera currently rendering; swaps when the projection changes. */
  get camera(): PerspectiveCamera | OrthographicCamera {
    return this.activeProjection === 'orthographic'
      ? this.orthographicCamera
      : this.perspectiveCamera;
  }

  get projection(): Projection {
    return this.activeProjection;
  }

  /**
   * Switches projection while holding the view still: the new camera
   * inherits the old one's placement, and its framing is translated so the
   * model keeps the same apparent size.
   */
  setProjection(next: Projection): void {
    if (next === this.activeProjection) return;
    const from = this.camera;
    const target = this.controls.target;
    const distance = from.position.distanceTo(target);

    if (next === 'orthographic') {
      this.orthoHeight = visibleHeightAtDistance(distance, this.perspectiveCamera.fov);
      this.orthographicCamera.zoom = 1;
      this.orthographicCamera.position.copy(from.position);
      this.orthographicCamera.quaternion.copy(from.quaternion);
      this.orthographicCamera.up.copy(from.up);
    } else {
      // an orthographic camera zooms by scaling its frustum, so undo that
      // before asking how far away the same view sits in perspective
      const height = this.orthoHeight / this.orthographicCamera.zoom;
      const back = distanceForVisibleHeight(height, this.perspectiveCamera.fov);
      const dir = from.position.clone().sub(target);
      if (dir.lengthSq() === 0) dir.set(1, 0.8, 1);
      this.perspectiveCamera.position.copy(target).add(dir.normalize().multiplyScalar(back));
      this.perspectiveCamera.quaternion.copy(from.quaternion);
      this.perspectiveCamera.up.copy(from.up);
    }

    this.activeProjection = next;
    this.controls.object = this.camera;
    this.applyCameraFrustum();
    this.controls.update();
    this.invalidate();
  }

  /**
   * Double-click pivots on whatever is under the pointer, panning it to the
   * middle of the screen without changing the viewing angle or distance.
   */
  private readonly onDoubleClick = (e: MouseEvent): void => {
    if (!this.selection.isEnabled) return;
    const point = this.selection.raycastPoint(e.clientX, e.clientY);
    if (!point) return;
    const { position, target } = recenterOn(this.camera.position, this.controls.target, point);
    this.camera.position.copy(position);
    this.controls.target.copy(target);
    // the drift leash follows the user to wherever they are working
    this.controls.cursor.copy(target);
    this.controls.update();
    this.invalidate();
  };

  /**
   * Moves the pivot to the depth of the given objects without shifting the
   * view: orbiting starts turning around what you picked rather than the
   * middle of the model.
   */
  pivotOnObjects(objects: Object3D[]): void {
    if (objects.length === 0) return;
    const box = new Box3();
    for (const obj of objects) box.expandByObject(obj);
    if (box.isEmpty()) return;
    const forward = new Vector3();
    this.camera.getWorldDirection(forward);
    const pivot = pivotAtDepth(this.camera.position, forward, box.getCenter(new Vector3()));
    this.controls.target.copy(pivot);
    this.controls.update();
    this.invalidate();
  }

  /** Request a render on the next frame (on-demand rendering). */
  invalidate(): void {
    this.needsRender = true;
  }

  private startLoop(): void {
    const tick = (): void => {
      if (this.disposed) return;
      const now = performance.now();

      // with damping on, update() keeps reporting true while the motion eases
      const controlsMoved = this.controls.update();
      if (this.needsRender || controlsMoved) {
        this.needsRender = false;
        this.renderer.render(this.scene, this.camera);
        this.renderedFrames++;
      }

      if (this.lastStatsEmitTs === 0) this.lastStatsEmitTs = now;
      const elapsed = now - this.lastStatsEmitTs;
      if (elapsed >= 500 && this.statsListener) {
        const idle = this.renderedFrames === 0;
        const fps = idle ? 0 : Math.round((this.renderedFrames / elapsed) * 1000);
        const { total, visible } = meshStats(this.modelRoot);
        this.statsListener({
          fps,
          idle,
          triangles: this.renderer.info.render.triangles,
          drawCalls: this.renderer.info.render.calls,
          totalMeshes: total,
          visibleMeshes: visible,
        });
        this.renderedFrames = 0;
        this.lastStatsEmitTs = now;
      }

      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  onStats(listener: StatsListener | null): void {
    this.statsListener = listener;
  }

  /** Fires whenever the camera moves, for widgets that mirror its orientation. */
  onCameraChange(listener: (() => void) | null): void {
    this.cameraListener = listener;
  }

  fitToObject(direction = new Vector3(1, 0.8, 1)): void {
    const box = new Box3().setFromObject(this.modelRoot);
    if (box.isEmpty()) return;

    const sphere = new Sphere();
    box.getBoundingSphere(sphere);
    const { center, radius } = sphere;
    if (radius === 0) return;

    const fovRad = (this.perspectiveCamera.fov * Math.PI) / 180;
    const distance = radius / Math.sin(fovRad / 2);

    const dir = direction.clone().normalize();
    this.camera.position.copy(center).add(dir.multiplyScalar(distance));
    this.perspectiveCamera.near = Math.max(0.01, radius / 1000);
    this.perspectiveCamera.far = Math.max(VIEWER_CONFIG.far, distance * 4);
    // distance frames a perspective view; an orthographic one is framed by
    // its frustum, so size that to the same sphere
    this.orthoHeight = 2 * radius * 1.05;
    this.orthographicCamera.zoom = 1;
    this.applyCameraFrustum();
    // zoomToCursor drags the target about; keep it near the model so the
    // pivot cannot wander off after a lot of zooming at empty space
    this.controls.cursor.copy(center);
    this.controls.maxTargetRadius = radius * VIEWER_CONFIG.maxPivotDrift;

    this.controls.target.copy(center);
    this.controls.update();
    this.invalidate();
  }

  topView(): void {
    this.fitToObject(directionForView('top'));
  }

  /** Zoom to one or more objects, keeping the current viewing direction. */
  focusOn(target: Object3D | Object3D[]): void {
    const objects = Array.isArray(target) ? target : [target];
    const box = new Box3();
    for (const obj of objects) box.expandByObject(obj);
    if (box.isEmpty()) return;

    const sphere = new Sphere();
    box.getBoundingSphere(sphere);
    const { center, radius } = sphere;
    if (radius === 0) return;

    const fovRad = (this.perspectiveCamera.fov * Math.PI) / 180;
    const distance = (radius / Math.sin(fovRad / 2)) * 1.2;

    const dir = this.camera.position.clone().sub(this.controls.target);
    if (dir.lengthSq() === 0) dir.set(1, 0.8, 1);
    dir.normalize();

    this.camera.position.copy(center).add(dir.multiplyScalar(distance));
    this.perspectiveCamera.near = Math.max(0.01, radius / 1000);
    this.perspectiveCamera.far = Math.max(this.perspectiveCamera.far, distance * 4);
    this.orthoHeight = 2 * radius * 1.2;
    this.orthographicCamera.zoom = 1;
    this.applyCameraFrustum();

    this.controls.target.copy(center);
    this.controls.update();
    this.invalidate();
  }

  setMeasureMode(on: boolean): void {
    if (on) this.selection.deselect();
    this.selection.setEnabled(!on);
    this.measurements.setActive(on);
    this.canvas.style.cursor = on ? 'crosshair' : '';
    this.invalidate();
  }

  setSectionPlane(plane: Plane | null): void {
    this.renderer.clippingPlanes = plane ? [plane] : [];
    this.invalidate();
  }

  modelBox(): Box3 {
    return new Box3().setFromObject(this.modelRoot);
  }

  setEdges(enabled: boolean): void {
    this.edgesEnabled = enabled;
    if (enabled) this.ensureEdges();
    for (const mesh of collectMeshes(this.modelRoot)) {
      const edges = mesh.children.find((c) => c.name === 'edges');
      if (edges) edges.visible = enabled;
    }
    this.invalidate();
  }

  get edges(): boolean {
    return this.edgesEnabled;
  }

  private ensureEdges(): void {
    for (const mesh of collectMeshes(this.modelRoot)) {
      if (mesh.children.some((c) => c.name === 'edges')) continue;
      const lines = new LineSegments(new EdgesGeometry(mesh.geometry, 30), this.edgeMaterial);
      lines.name = 'edges';
      // edge overlays must never intercept picking
      lines.raycast = () => {};
      mesh.add(lines);
    }
  }

  resetView(): void {
    this.selection.deselect();
    this.showAll();
    this.fitToObject();
  }

  showAll(): void {
    showAllMeshes(this.modelRoot);
    this.invalidate();
  }

  async screenshot(filename = 'ifc-navigator.png'): Promise<void> {
    this.renderer.render(this.scene, this.camera);
    const dataUrl = this.renderer.domElement.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  clearModel(): void {
    this.selection.deselect();
    disposeModelTree(this.modelRoot);
    this.invalidate();
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.resizeObserver?.disconnect();
    this.canvas.removeEventListener('dblclick', this.onDoubleClick);
    this.selection.dispose();
    this.measurements.dispose();
    this.clearModel();
    this.edgeMaterial.dispose();
    this.controls.dispose();
    this.renderer.dispose();
  }
}
